/**
 * ActivitySubTabs Component
 *
 * Sub-tab navigation for the Activity tab (History / Statistics).
 *
 * @example
 * ```tsx
 * <ActivitySubTabs
 *   activeView="history"
 *   onViewChange={(view) => setActiveView(view)}
 * />
 * ```
 */

import type { CSSProperties } from 'react';

/**
 * Activity view type - either history or statistics
 */
export type ActivityView = 'history' | 'statistics';

interface ActivitySubTabsProps {
  /** Currently active view */
  activeView: ActivityView;
  /** Callback when view changes */
  onViewChange: (view: ActivityView) => void;
}

/**
 * ActivitySubTabs provides sub-navigation between History and Statistics views
 */
export function ActivitySubTabs({ activeView, onViewChange }: ActivitySubTabsProps) {
  const tabs: { id: ActivityView; label: string }[] = [
    { id: 'history', label: 'History' },
    { id: 'statistics', label: 'Statistics' },
  ];

  return (
    <div style={containerStyle}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          style={{
            ...tabStyle,
            ...(activeView === tab.id ? activeTabStyle : {}),
          }}
          onClick={() => onViewChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--spacing-xs)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  backgroundColor: 'var(--color-bg-secondary)',
};

const tabStyle: CSSProperties = {
  flex: 1,
  padding: 'var(--spacing-sm)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  border: 'none',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
};

const activeTabStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-primary)',
};

export default ActivitySubTabs;
